import React, { useEffect, useCallback, useState } from 'react';
import { X, Download, Loader2, Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ContentType } from '@/types/content-unlock';

export interface UnlockedContentDisplayProps {
  contentId: string;
  contentType: ContentType;
  mediaUrl: string;
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export function UnlockedContentDisplay({
  contentId,
  contentType,
  mediaUrl,
  title,
  isOpen,
  onClose,
  className,
}: UnlockedContentDisplayProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const togglePlayPause = useCallback(() => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsVideoPlaying(true);
    } else {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if (e.key === ' ' && contentType === 'media') {
        e.preventDefault();
        togglePlayPause();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, contentType, togglePlayPause]);

  // Reset state when media URL changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [mediaUrl]);

  const handleMediaLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleMediaError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    console.error(`Failed to load media: ${contentId}`);
  }, [contentId]);

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = title || `content-${contentId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [mediaUrl, title, contentId]);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleFullscreen = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  const isVideo = contentType === 'media' && mediaUrl.match(/\.(mp4|webm|mov|m4v)($|\?)/i);
  const isImage = !isVideo;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/95',
        className
      )}
      onClick={handleBackdropClick}
      data-testid="unlocked-content-display"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Media viewer'}
    >
      {/* Top toolbar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent z-10">
        <div className="flex-1 min-w-0">
          {title && (
            <h2
              className="text-white font-medium truncate"
              data-testid="content-title"
            >
              {title}
            </h2>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="download-button"
            aria-label="Download"
          >
            <Download className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="close-button"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          data-testid="loading-indicator"
        >
          <Loader2 className="h-10 w-10 text-white animate-spin" />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div
          className="flex flex-col items-center justify-center gap-3 text-white"
          data-testid="error-state"
        >
          <p className="text-lg">Failed to load content</p>
          <Button variant="outline" onClick={() => setHasError(false)}>
            Retry
          </Button>
        </div>
      )}

      {/* Image display */}
      {isImage && !hasError && (
        <img
          src={mediaUrl}
          alt={title || 'Unlocked content'}
          className={cn(
            'max-w-[90vw] max-h-[90vh] object-contain transition-opacity',
            isLoading ? 'opacity-0' : 'opacity-100'
          )}
          onLoad={handleMediaLoad}
          onError={handleMediaError}
          data-testid="content-image"
        />
      )}

      {/* Video display */}
      {isVideo && !hasError && (
        <div className="relative max-w-[90vw] max-h-[90vh]">
          <video
            ref={videoRef}
            src={mediaUrl}
            className={cn(
              'max-w-[90vw] max-h-[90vh] object-contain transition-opacity',
              isLoading ? 'opacity-0' : 'opacity-100'
            )}
            onLoadedData={handleMediaLoad}
            onError={handleMediaError}
            onPlay={() => setIsVideoPlaying(true)}
            onPause={() => setIsVideoPlaying(false)}
            onEnded={() => setIsVideoPlaying(false)}
            playsInline
            data-testid="content-video"
          />

          {/* Video controls overlay */}
          {!isLoading && (
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-3 p-4 bg-gradient-to-t from-black/60 to-transparent">
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePlayPause}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="play-pause-button"
                aria-label={isVideoPlaying ? 'Pause' : 'Play'}
              >
                {isVideoPlaying ? (
                  <Pause className="h-6 w-6" />
                ) : (
                  <Play className="h-6 w-6" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMute}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="mute-button"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <VolumeX className="h-5 w-5" />
                ) : (
                  <Volume2 className="h-5 w-5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFullscreen}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="fullscreen-button"
                aria-label="Fullscreen"
              >
                <Maximize className="h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
